import { useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";
import { Reveal, SectionTitle, Overline } from "@/components/Reveal";
import { genChartData, genUpdates } from "@/data/mock";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-gold px-4 py-3 !rounded-xl">
      <div className="text-xs text-white/50">{label}</div>
      <div className="font-display font-bold text-[#F5C542]">
        {payload[0].value.toFixed(2)}%
      </div>
    </div>
  );
};

export const RtpChart = () => {
  const [data] = useState(() => genChartData());
  const [updates] = useState(() => genUpdates());

  return (
    <section
      id="estatisticas"
      className="relative py-20 md:py-28"
      data-testid="chart-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="mb-10">
          <Overline>Histórico</Overline>
          <SectionTitle className="mt-3">
            Evolução <span className="text-gold-gradient">dos Sinais</span>
          </SectionTitle>
          <p className="mt-3 text-white/50 max-w-lg font-body">
            Variação dos sinais nas últimas 24 horas.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-[1.7fr_1fr] gap-6">
          <Reveal>
            <div className="glass-gold p-5 md:p-7 h-full" data-testid="rtp-chart">
              <div className="h-[300px] md:h-[360px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#F5C542" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#F5C542" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis
                      dataKey="hora"
                      tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      interval={3}
                    />
                    <YAxis
                      domain={[88, 100]}
                      tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#F5C542", strokeOpacity: 0.3 }} />
                    <Area
                      type="monotone"
                      dataKey="rtp"
                      stroke="#F5C542"
                      strokeWidth={3}
                      fill="url(#goldFill)"
                      dot={false}
                      activeDot={{ r: 5, fill: "#F5C542", stroke: "#120D0D", strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="glass-card p-5 md:p-6 h-full">
              <div className="font-display font-bold text-white mb-5 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#00FF41] animate-pulse" />
                Últimas Atualizações
              </div>
              <div className="space-y-3">
                {updates.map((u, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]"
                    data-testid={`update-item-${i}`}
                  >
                    <div>
                      <div className="text-sm font-medium text-white">{u.game}</div>
                      <div className="text-[11px] text-white/40">{u.provider} • {u.time}</div>
                    </div>
                    <div
                      className="font-display font-bold text-sm"
                      style={{ color: u.level.color }}
                    >
                      {u.rtp.toFixed(2)}%
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
